// src/api/auth.ts
/**
 * Authentication API functions.
 * Uses the centralized Axios instance defined in `src/api/client.ts` to ensure
 * consistent configuration (base URL, interceptors, error mapping, etc.).
 * 
 * NOTE: Endpoint paths do NOT include /api/v1 prefix since the base URL from
 * environment variables already includes it (e.g., http://localhost:8081/api/v1)
 */
import { api } from './client';
import type { LoginRequestDTO, LoginResponseDTO } from '../types';

/**
 * Perform a login request.
 * @param payload - The user's credentials.
 * @returns The login response containing access/refresh tokens and user info.
 * @throws Will re‑throw any network or server error after being mapped by the
 *         client interceptor.
 */
export const login = async (payload: LoginRequestDTO): Promise<LoginResponseDTO> => {
  const response = await api.post<LoginResponseDTO>('/auth/login', payload);
  return response.data;
};

/**
 * Refresh the JWT access token using a refresh token.
 * @param refreshToken - The refresh token previously obtained from login.
 * @returns A new {@link LoginResponseDTO} with fresh tokens.
 */
export const refreshToken = async (refreshToken: string): Promise<LoginResponseDTO> => {
  const response = await api.post<LoginResponseDTO>('/auth/refresh', { refreshToken });
  return response.data;
};

/**
 * Log the user out.
 * The request is sent to the backend which will invalidate the refresh token.
 * The client interceptor automatically attaches the current access token.
 */
export const logout = async (): Promise<void> => {
  await api.post('/auth/logout');
};